import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import emailjs from '@emailjs/browser';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const PromptGuidePage = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !hasAcceptedTerms) return;

    setIsSubmitting(true);
    try {
      // Send email with the guide
      await emailjs.send(
        'default_service',
        'template_prompt_guide',
        {
          user_email: email,
          guide_url: 'https://lumilabs.es/assets/archivo-ejemplo.pdf', // Replace with actual URL
        }
      );

      toast.success('¡Guía enviada! Revisa tu email');
      setEmail('');
      setHasAcceptedTerms(false);
    } catch (error) {
      console.error('Error sending guide:', error);
      toast.error('Error al enviar la guía. Por favor, inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-purple-50 pt-24 pb-20">
      <div className="container mx-auto px-6">
        <Link 
          to="/" 
          className="inline-flex items-center text-cyan-600 hover:text-purple-600 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Volver al inicio
        </Link>

        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-cyan-500 to-purple-600 bg-clip-text text-transparent">
                Guía de Prompting
              </h1>
              <p className="text-xl text-gray-600 mb-6">
                Descarga nuestra guía gratuita y aprende a escribir prompts efectivos para sacar el máximo partido a la IA generativa.
              </p>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email profesional
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600 w-5 h-5" />
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="nombre@empresa.com"
                        className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={hasAcceptedTerms}
                      onChange={(e) => setHasAcceptedTerms(e.target.checked)}
                      className="mt-1"
                      required
                    />
                    <label htmlFor="terms" className="ml-2 text-sm text-gray-600">
                      He leído y acepto la política de privacidad y el envío de comunicaciones
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || !email || !hasAcceptedTerms}
                    className="w-full px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-lg hover:from-cyan-600 hover:to-purple-700 transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      'Enviando...'
                    ) : (
                      <>
                        <FileText className="w-5 h-5 mr-2" />
                        Descargar Guía
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[3/4] rounded-xl overflow-hidden border-4 border-white shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=800"
                  alt="Guía de Prompting"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default PromptGuidePage;